import { useState, useCallback, useEffect, useRef } from 'react';
import { api, subscribeToRecordChanges } from '../api/client';

export function useRecords(enabled: boolean) {
  const [records, setRecords] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRecords = useCallback(async (search?: string) => {
    try {
      const data = await api.getRecords(search);
      setRecords(data.records);
      setFetchError(null);
    } catch (err: any) {
      setFetchError(err.message || 'Failed to fetch records');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveRecord = useCallback(async (metadata: any, stages: any, inspectorName?: string) => {
    const data = await api.saveRecord({ metadata, stages, inspector_name: inspectorName });
    await fetchRecords();
    return data.record;
  }, [fetchRecords]);

  const deleteRecord = useCallback(async (id: string) => {
    await api.deleteRecord(id);
    await fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    if (!enabled) {
      setRecords([]);
      setIsLoading(false);
      setFetchError(null);
      return;
    }

    setIsLoading(true);
    fetchRecords();
    const unsubscribe = subscribeToRecordChanges(() => fetchRecords());

    intervalRef.current = setInterval(() => {
      fetchRecords();
    }, 15000);

    const onFocus = () => fetchRecords();
    window.addEventListener('focus', onFocus);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      unsubscribe();
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, fetchRecords]);

  return { records, isLoading, fetchError, saveRecord, deleteRecord, refreshRecords: fetchRecords };
}
